import React, { ReactElement } from 'react';

import { Endpoint, ReportService, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { FieldAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { IndiceReajusteAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface IndiceReajuste {
  dsTipoContratacao: string,
  ieStatus: IeStatusIndiceReajuste,
  iePossuiArquivo: string,
  nrSeqReajDoc: number,
  nrSeqRelatorio: number,
  dsArquivo: string,
  dtMesReajuste: string,
  txReajust: string,
  ieApresentarDtLibReajuste: string,
}

interface DadosRelatorio {
  cdClassifRelat: string,
  cdRelatorio: string,
}

const OptionKey = {
  DETALHES: 'detalhes',
  IMPRIMIR: 'imprimir',
  DOWNLOAD: 'download',
};

enum IeStatusIndiceReajuste {
  PROVISORIO = '1',
  DEFINITIVO = '2',
  CALCULADO = '3',
}

@Controller({ fragment: FragmentNames.INDICE_REAJUSTE })
export default class IndiceReajusteController extends BaseController {

  @OnMount(Aliases.PANEL_INDICE_REAJUSTE)
  OnMountLayout(): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqContrato });
  }

  @OnMount(Aliases.FIELD_RELEASE_DATE)
  onMountReleaseDate(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { ieApresentarDtLibReajuste } = api.getData() as unknown as IndiceReajuste;
    api.setVisible(ieApresentarDtLibReajuste == 'S');
  }

  @OnMount(Aliases.FIELD_REAJUSTE)
  onMountFieldReajuste(event: MountEvent): void {
    const api = event.target as unknown as FieldAPI;
    const { txReajust: taxaReajuste } = api.getData() as unknown as IndiceReajuste;

    if (isEmptyOrBlankString(taxaReajuste)) {
      api.setVisible(false);
    }
  }

  @HPMSOnCreateOption(Aliases.CARD_INDICE)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { iePossuiArquivo } = data as unknown as IndiceReajuste;

    const show = {
      [OptionKey.DETALHES]: true,
      [OptionKey.IMPRIMIR]: true,
      [OptionKey.DOWNLOAD]: iePossuiArquivo == 'S',
    };

    shouldRender(show[key]);
  }

  @OnDynamicContentRender(Aliases.CARD_INDICE)
  onRenderStatus(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsTipoContratacao } = data as IndiceReajuste;

    render((): ReactElement => <Stamp
      icon={this.getIndiceReajusteIcon(ieStatus)}
      color={this.getIndiceReajusteColor(ieStatus)}
      label={dsTipoContratacao} />);
  }

  @HPMSOnOptionClick(Aliases.CARD_INDICE)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqReajDoc, nrSeqRelatorio, dsArquivo } = data as unknown as IndiceReajuste;

    const action = {
      [OptionKey.DETALHES]: () => this.onOpenModalDocumento(nrSeqReajDoc),
      [OptionKey.DOWNLOAD]: () => this.onDownloadClick(nrSeqReajDoc, dsArquivo),
      [OptionKey.IMPRIMIR]: () => this.onPrint(nrSeqReajDoc, nrSeqRelatorio),

    };

    if (action[key]) {
      action[key]();
    }
  }

  onDownloadClick(nrSeqReajDoc: number, dsArquivo: string): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    FileService.download(Endpoint.GET.INDICE_REAJUSTE_DOWNLOAD, dsArquivo, { nrSeqContrato, nrSeqReajDoc }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  onOpenModalDocumento(nrSeqReajDoc: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      closeable: true,
      fragment: 'indice-reajuste-ds-documentacao',
      title: '',
      contextData: { nrSeqReajDoc, nrSeqContrato },
    });

  }

  getIndiceReajusteColor(ieStatus: IeStatusIndiceReajuste): Color {
    const color = {
      [IeStatusIndiceReajuste.PROVISORIO]: Color.SAFETY_BLUE,
      [IeStatusIndiceReajuste.DEFINITIVO]: Color.SAFETY_GREEN,
      [IeStatusIndiceReajuste.CALCULADO]: Color.SAFETY_YELLOW,
    };
    return color[ieStatus];
  }

  getIndiceReajusteIcon(ieStatus: IeStatusIndiceReajuste): StampIcon {
    const icon = {
      [IeStatusIndiceReajuste.PROVISORIO]: StampIcon.CLOCK,
      [IeStatusIndiceReajuste.DEFINITIVO]: StampIcon.CHECK,
      [IeStatusIndiceReajuste.CALCULADO]: StampIcon.WARNING,
    };

    return icon[ieStatus];
  }

  onPrint(nrSeqReajDoc: number, nrSeqRelatorio: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const reportService = new ReportService('');

    const params = {
      nrSeqContrato: nrSeqContrato,
      NR_SEQUENCIA: nrSeqReajDoc,
    };

    if (nrSeqRelatorio) {

      void HttpService.get(Endpoint.GET.DADOS_RELATORIO_BY_SEQ, { nrSeqContrato, nrSeqRelatorio })
        .then(response => {
          const { cdClassifRelat, cdRelatorio } = response.body as DadosRelatorio;
          void reportService.gerarRelatorio(cdRelatorio, cdClassifRelat, params);
        });

    } else {
      void reportService.gerarRelatorio('4873', 'WPLS', params);
    }

  }

}
